import React, { useState, useEffect } from 'react'
import Button from 'components/Button/Button'
import { Input } from 'components/Input'
import {
  EmailOutreachButtonContainer,
  EmailOutreachContentContainer,
  EmailOutreachInputContainer,
} from './EmailOutreach.styled'
import FacilityFormTemplate from 'templates/FacilityFormTemplate'
import RichText from 'components/RichText'
import cogoToast from 'cogo-toast'
import { useAppDispatch, useAppState } from 'store'
import { outreachActions } from 'store/slices/outreach'
import { useParams } from 'react-router'
import { Location } from 'models/location'
import Spinner from 'components/Spinner'
import { useAmplitude } from 'hooks/useAmplitude'
import { AMPLITUDE_EVENTS } from 'amplitude/events'
import { appointmentActions } from 'store/slices/appointments/appointments.slice'
import Select from 'components/Select'
import { SelectTypes } from 'components/Select/Select.props'
import { Col, Row } from 'react-grid-system'
import { Client } from 'models/client.model'
import { clientActions } from 'store/slices/clients/clients.slice'
import { programActions } from 'store/slices/programs'
import { Program } from 'models/program.model'

const ALL_CLIENTS_VALUE = 'AC', CUSTOM_VALUE = 'CM', PROGRAM_VALUE = 'PM';
const recipientsOptions: SelectTypes[] = [
  { label: 'All Clients', value: ALL_CLIENTS_VALUE },
  { label: 'Custom', value: CUSTOM_VALUE },
  { label: 'Program', value: PROGRAM_VALUE },
]

const EmailOutreachPage = () => {
  const params: Location = useParams()
  const dispatch = useAppDispatch()
  const { location } = useAppState(state => state.appointments)
  const { emailStatus } = useAppState(state => state.outreach)
  const { players, playersLoading } = useAppState(state => state.clients)
  const { programs, programStatus } = useAppState(state => state.programs)

  const [email, setEmail] = useState<string>()
  const [subject, setSubject] = useState<string>()
  const [body, setBody] = useState<string>()
  const [recipientSelectedInfo, setRecipientSelectedInfo] = useState<SelectTypes | undefined>(undefined)
  const [recipientExtraList, setRecipientExtraList] = useState<SelectTypes[]>([])
  const [recipientExtraSelected, setRecipientExtraSelected] = useState<SelectTypes[]>([])

  const { logEvent } = useAmplitude()

  useEffect(() => {
    dispatch(appointmentActions.getLocation(params ? params.locationId : ''))
    logEvent(AMPLITUDE_EVENTS.EMAIL_OUTREACH_PAGE_EVENT, { location: params.locationId })
  }, [])

  useEffect(() => {
    setEmail(location?.email);
  }, [location])

  useEffect(() => {
    if (players && players.length > 0) {
      const newPlayerList = players.map((player: Client) => {
        return { label: player.title, value: player.id.toString() }
      })
      setRecipientExtraList(newPlayerList)
    }
  }, [players])

  const handleExtraRecipients = (text: string, customOption: boolean) => {
    if (customOption && text.length >= 3) {
      dispatch(
        clientActions.getClientPlayers({
          locationId: params.locationId,
          page: 0,
          limit: 100,
          search_text: text,
        })
      )
    }
    return
  }

  const handleRecipientSelected = (option: SelectTypes) => {
    setRecipientExtraList([]);
    setRecipientExtraSelected([]);
    if (option && option.value !== '') {
      setRecipientSelectedInfo(option);
      if (option.value === PROGRAM_VALUE) {
        handleExtraRecipientProgramCombo();
      }
    } else {
      setRecipientSelectedInfo(undefined);
    }

  }

  const handleExtraRecipientProgramCombo = () => {
    if (!programs) {
      dispatch(
        programActions.getPrograms({
          locationId: params ? params.locationId : '',
          discarded: false,
          visible: true
        })
      ).then((res: any) => {
        if (res && res.payload && res.payload.data.length > 0) {
          handleProgramData(res.payload.data);
        }
      });
    } else {
      handleProgramData(programs);
    }
  }

  const handleProgramData = (programs: Program[]) => {
    if (programs && programs.length > 0) {
      const newProgramList = programs.map((program: Program) => {
        return { label: program.name, value: program.id.toString() }
      })
      setRecipientExtraList(newProgramList)
    }
  }


  const handleSendMessage = () => {
    if (body && subject && recipientSelectedInfo) {

      if (recipientSelectedInfo.value !== ALL_CLIENTS_VALUE
        && recipientExtraSelected && recipientExtraSelected.length === 0) {
        cogoToast.error('Recipients contacts are required.', {
          position: 'top-right',
        })
        return
      }

      const contacts = recipientExtraSelected?.map((item) => Number(item.value));
      const recipientData = handleRecipientData(recipientSelectedInfo.value, contacts);
      dispatch(
        outreachActions.sendEmails({
          locationId: params ? params.locationId : '',
          body: { body, subject, ...recipientData }
        })
      )
    } else {
      cogoToast.error('Recipients, Subject and Body fields are required.', {
        position: 'top-right',
      })
    }
  }

  const handleRecipientData = (option: string, contacts: number[]) => {
    switch (option) {
      case PROGRAM_VALUE: return {
        recipient_ids: null,
        program_ids: contacts,
        is_all_clients: false
      };
      case CUSTOM_VALUE: return {
        recipient_ids: contacts,
        program_ids: null,
        is_all_clients: false
      };
      default: return {
        recipient_ids: null,
        program_ids: null,
        is_all_clients: true
      };
    }
  }

  return (
    <FacilityFormTemplate>
      {emailStatus === 'loading' && <Spinner />}
      {emailStatus !== 'loading' && (
        <EmailOutreachContentContainer>
          <EmailOutreachInputContainer>
            <Input
              label={'From'}
              placeholder="info@my-facility.com"
              value={email}
              disabled />
          </EmailOutreachInputContainer>
          <Row>
            <Col xs={6} sm={6} md={6} lg={6}>
              <Select
                autocomplete
                options={recipientsOptions}
                label="Recipients"
                onChange={recipient => handleRecipientSelected(recipient as SelectTypes)}
                required
              />
            </Col>
            {recipientSelectedInfo && (recipientSelectedInfo.value === PROGRAM_VALUE || recipientSelectedInfo.value === CUSTOM_VALUE) &&
              (<Col xs={6} sm={6} md={6} lg={6}>
                <Select
                  required
                  autocomplete
                  isMulti
                  options={recipientExtraList}
                  placeholder={`Select ${recipientSelectedInfo.value === CUSTOM_VALUE ? 'Player Name' : recipientSelectedInfo.label}`}
                  onInputChange={text => handleExtraRecipients(text, recipientSelectedInfo.value === CUSTOM_VALUE)}
                  isLoading={(playersLoading === 'loading' || programStatus === 'loading') ? true : false}
                  onChange={option =>
                    setRecipientExtraSelected(option as SelectTypes[])
                  }
                />
              </Col>)
            }
          </Row>
          <div style={{ marginBottom: 36, width: '75%' }}>
            <Input
              label={'Subject'}
              placeholder="Subject"
              onTextChange={setSubject}
              required
            />
          </div>

          <RichText label={'Body'} required onChange={setBody} />

          <EmailOutreachButtonContainer>
            <Button
              preset="filled"
              variant="primary"
              text="Send Email"
              onClick={handleSendMessage}
              size={'medium'}
            />
          </EmailOutreachButtonContainer>
        </EmailOutreachContentContainer>
      )}
    </FacilityFormTemplate>
  )
}

export default EmailOutreachPage
